<template>
  <Modal
      :style="computedWindowSize"
      :visible="visible"
      class="p-fluid system-subscription-modal"
      @close="close">
    <template #header>
      <div class="p-dialog-title">
        {{ $t('billing.subscriptions.system.editTitle') }}
      </div>
    </template>

    <Spinner v-if="modalDataIsLoading || dataIsSending"/>

    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <InputText
              id="system-subscription-tenant"
              :modelValue="itemData.tenant_name || ''"
              disabled/>
          <label for="system-subscription-tenant">{{ $t('Tenant') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <InputText
              id="system-subscription-location"
              :modelValue="itemData.location_name || ''"
              disabled/>
          <label for="system-subscription-location">{{ $t('Location') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <InputText
              id="system-subscription-company"
              :modelValue="itemData.company_name || ''"
              disabled/>
          <label for="system-subscription-company">{{ $t('Company') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="system-subscription-plan"
                          v-model="selectedPlan"
                          :options="planOptions"
                          :preventFilter="true"
                          :filter="false"
                          data-key="code"
                          optionLabel="key"
                          :showClear="false"
                          :class="{'p-invalid': submitted && !selectedPlan}"
                          @show="toggleDropdownOverlayVisibility(true)"
                          @hide="toggleDropdownOverlayVisibility(false)">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <strong>{{ slotProps.value.key }}</strong>

                <span class="p-ml-2">
                  {{ formatBillingCurrency(slotProps.value.price, 'EUR', 0, 2) }}
                  {{ $t('billing.planChangeModal.pricePeriodShort') }}
                </span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>

            <template #option="slotProps">
              <div class="p-d-flex p-jc-between p-ai-center">
                <strong>{{ slotProps.option.key }}</strong>

                <span class="p-ml-3">
                  {{ formatBillingCurrency(slotProps.option.price, 'EUR', 0, 2) }}
                  {{ $t('billing.planChangeModal.pricePeriodShort') }}
                </span>
              </div>
            </template>
          </CustomDropdown>
          <label for="system-subscription-plan">
            {{ $t('billing.subscriptions.planLabel') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small v-if="submitted && !selectedPlan" class="p-invalid">
          {{ $t('Required field') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown
              id="system-subscription-status"
              v-model="selectedStatus"
              :options="statusOptions"
              :preventFilter="true"
              :filter="false"
              :showClear="false"
              :class="{'p-invalid': submitted && !selectedStatus}"
              @show="toggleDropdownOverlayVisibility(true)"
              @hide="toggleDropdownOverlayVisibility(false)">
            <template #value="slotProps">
              <span v-if="slotProps.value"
                    class="state-badge"
                    :class="statusClass(slotProps.value.key)">
                {{ statusLabel(slotProps.value.key) }}
              </span>
            </template>

            <template #option="slotProps">
              <span class="state-badge"
                    :class="statusClass(slotProps.option.key)">
                {{ statusLabel(slotProps.option.key) }}
              </span>
            </template>
          </CustomDropdown>
          <label for="system-subscription-status">
            {{ $t('Status') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small v-if="submitted && !selectedStatus" class="p-invalid">
          {{ $t('Required field') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <InputNumber
              id="system-subscription-current-amount"
              :modelValue="currentAmount"
              :locale="computedNumberInputLocale"
              mode="currency"
              currency="EUR"
              :minFractionDigits="2"
              :maxFractionDigits="2"
              disabled/>
          <label for="system-subscription-current-amount">
            {{ $t('billing.subscriptions.currentMonthlyAmountLabel') }}
          </label>
        </div>
      </div>

      <div class="p-col-12 p-mt-3">
<!--        <hr class="p-mt-0">-->
        <div class="system-subscription-modal__section-title p-mb-4">
          {{ $t('Period') }}
        </div>
      </div>

      <div class="p-field p-col-12 p-md-6 p-mb-5">
        <div class="p-float-label">
          <Calendar
              id="system-subscription-period-start"
              v-model="currentPeriodStart"
              appendTo="self"
              class="calendar-bottom"
              :dateFormat="settings.dateFormat"
              :showIcon="true"
              :monthNavigator="true"
              :yearNavigator="true"
              :showButtonBar="true"
              :selectOtherMonths="true"
              autocomplete="off"
              @show="toggleDropdownOverlayVisibility(true)"
              @hide="toggleDropdownOverlayVisibility(false)"/>
          <label for="system-subscription-period-start">
            {{ $t('billing.invoices.periodStartLabel') }}
          </label>
        </div>
      </div>

      <div class="p-field p-col-12 p-md-6 p-mb-5">
        <div class="p-float-label">
          <Calendar
              id="system-subscription-period-end"
              v-model="currentPeriodEnd"
              appendTo="self"
              class="calendar-bottom"
              :dateFormat="settings.dateFormat"
              :showIcon="true"
              :monthNavigator="true"
              :yearNavigator="true"
              :showButtonBar="true"
              :selectOtherMonths="true"
              :class="{'p-invalid': submitted && periodRangeInvalid}"
              autocomplete="off"
              @show="toggleDropdownOverlayVisibility(true)"
              @hide="toggleDropdownOverlayVisibility(false)"/>
          <label for="system-subscription-period-end">
            {{ $t('billing.invoices.periodEndLabel') }}
          </label>
        </div>
        <small v-if="submitted && periodRangeInvalid" class="p-invalid">
          {{ $t('billing.subscriptions.system.endDateMustBeAfterStartDateMsg') }}
        </small>
      </div>

      <div class="p-col-12 p-mt-3">
<!--        <hr class="p-mt-0">-->
        <div class="system-subscription-modal__section-title p-mb-4">
          {{ $t('billing.subscriptions.system.baseDiscountLabel') }}
        </div>
      </div>

      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <!--          suffix=" %"-->
          <InputNumber
              id="system-subscription-discount-percent"
              v-model="discountPercent"
              :locale="computedNumberInputLocale"
              mode="decimal"
              :min="0"
              :max="100"
              :minFractionDigits="0"
              :maxFractionDigits="2"/>
          <label for="system-subscription-discount-percent">
            {{ $t('Discount') }} %
          </label>
        </div>
      </div>

      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <Calendar
              id="system-subscription-discount-start"
              v-model="discountStartsAt"
              appendTo="self"
              class="calendar-bottom"
              :dateFormat="settings.dateFormat"
              :showIcon="true"
              :monthNavigator="true"
              :yearNavigator="true"
              :showButtonBar="true"
              :selectOtherMonths="true"
              autocomplete="off"
              @show="toggleDropdownOverlayVisibility(true)"
              @hide="toggleDropdownOverlayVisibility(false)"/>
          <label for="system-subscription-discount-start">
            {{ $t('billing.subscriptions.system.discountStartLabel') }}
          </label>
        </div>
      </div>

      <div class="p-field p-col-12 p-md-4 p-mb-5">
        <div class="p-float-label">
          <Calendar
              id="system-subscription-discount-end"
              v-model="discountEndsAt"
              appendTo="body"
              class="calendar-bottom"
              :dateFormat="settings.dateFormat"
              :showIcon="true"
              :monthNavigator="true"
              :yearNavigator="true"
              :showButtonBar="true"
              :selectOtherMonths="true"
              :class="{'p-invalid': submitted && discountRangeInvalid}"
              autocomplete="off"
              @show="toggleDropdownOverlayVisibility(true)"
              @hide="toggleDropdownOverlayVisibility(false)"/>
          <label for="system-subscription-discount-end">
            {{ $t('billing.subscriptions.system.discountEndLabel') }}
          </label>
        </div>
        <small v-if="submitted && discountRangeInvalid" class="p-invalid">
          {{ $t('billing.subscriptions.system.endDateMustBeAfterStartDateMsg') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-mb-5">
        <div class="p-float-label">
          <InputText
              id="system-subscription-discount-reason"
              v-model.trim="discountReason"
              maxlength="255"
              autocomplete="off"/>
          <label for="system-subscription-discount-reason">
            {{ $t('billing.subscriptions.system.discountReasonLabel') }}
          </label>
        </div>
      </div>

      <div class="p-col-12 p-mt-3">
<!--        <hr class="p-mt-0">-->
        <div class="system-subscription-modal__section-title p-mb-4">
          {{ $t('billing.subscriptions.scheduledPlanLabel') }}
        </div>
      </div>

      <div class="p-field p-col-12 p-md-6 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="system-subscription-pending-plan"
                          v-model="selectedPendingPlan"
                          :options="pendingPlanOptions"
                          data-key="code"
                          optionLabel="key"
                          :preventFilter="true"
                          :filter="false"
                          :showClear="true"
                          @show="toggleDropdownOverlayVisibility(true)"
                          @hide="toggleDropdownOverlayVisibility(false)">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <strong>{{ slotProps.value.key }}</strong>

                <span class="p-ml-2">
                  {{ formatBillingCurrency(slotProps.value.price, 'EUR', 0, 2) }}
                  {{ $t('billing.planChangeModal.pricePeriodShort') }}
                </span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>

            <template #option="slotProps">
              <div class="p-d-flex p-jc-between p-ai-center">
                <strong>{{ slotProps.option.key }}</strong>
                <span class="p-ml-3">
                  {{ formatBillingCurrency(slotProps.option.price, 'EUR', 0, 2) }}
                  {{ $t('billing.planChangeModal.pricePeriodShort') }}
                </span>
              </div>
            </template>
          </CustomDropdown>
          <label for="system-subscription-pending-plan">
            {{ $t('billing.subscriptions.scheduledPlanLabel') }}
          </label>
        </div>
      </div>

      <div class="p-field p-col-12 p-md-6 p-mb-5">
        <div class="p-float-label">
          <Calendar
              id="system-subscription-pending-plan-start"
              v-model="pendingPlanStartsAt"
              appendTo="body"
              class="calendar-bottom"
              :dateFormat="settings.dateFormat"
              :showIcon="true"
              :monthNavigator="true"
              :yearNavigator="true"
              :showButtonBar="true"
              :selectOtherMonths="true"
              :disabled="!selectedPendingPlan"
              :class="{'p-invalid': submitted && selectedPendingPlan && !pendingPlanStartsAt}"
              autocomplete="off"
              @show="toggleDropdownOverlayVisibility(true)"
              @hide="toggleDropdownOverlayVisibility(false)"/>
          <label for="system-subscription-pending-plan-start">
            {{ $t('billing.subscriptions.system.scheduledPlanStartLabel') }}
          </label>
        </div>
        <small v-if="submitted && selectedPendingPlan && !pendingPlanStartsAt" class="p-invalid">
          {{ $t('Required field') }}
        </small>
      </div>


<!--      //SUSPENSION REASON - DONT DELETE!-->
<!--      <div-->
<!--          v-if="selectedStatus?.key === 'suspended'"-->
<!--          class="p-field p-col-12 p-md-6 p-mb-5">-->
<!--        <div class="p-float-label">-->
<!--          <CustomDropdown-->
<!--              id="system-subscription-lock-reason"-->
<!--              v-model="selectedLockReason"-->
<!--              :options="lockReasonOptions"-->
<!--              :preventFilter="true"-->
<!--              :filter="false"-->
<!--              :showClear="true"-->
<!--              @show="toggleDropdownOverlayVisibility(true)"-->
<!--              @hide="toggleDropdownOverlayVisibility(false)">-->
<!--            <template #value="slotProps">-->
<!--              <span v-if="slotProps.value">{{ lockReasonLabel(slotProps.value.key) }}</span>-->
<!--              <span v-else style="visibility: hidden">.</span>-->
<!--            </template>-->
<!--            <template #option="slotProps">-->
<!--              <span>{{ lockReasonLabel(slotProps.option.key) }}</span>-->
<!--            </template>-->
<!--          </CustomDropdown>-->
<!--          <label for="system-subscription-lock-reason">-->
<!--            {{ $t('billing.subscriptions.system.lockReasonLabel') }}-->
<!--          </label>-->
<!--        </div>-->
<!--      </div>-->

      <div class="p-field p-col-12 p-mb-5">
        <div class="p-d-flex p-ai-center">
          <Checkbox
              id="system-subscription-cancel-at-period-end"
              v-model="cancelAtPeriodEnd"
              :binary="true"/>
          <label for="system-subscription-cancel-at-period-end" class="pointer p-ml-2">
            {{ $t('billing.subscriptions.system.cancelAtPeriodEndLabel') }}
          </label>
        </div>
        <small v-if="submitted && cancelAtPeriodEnd && !currentPeriodEnd" class="p-invalid p-d-block p-mt-2">
          {{ $t('billing.subscriptions.system.periodEndRequiredForCancellationMsg') }}
        </small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <LimitedCharsTextarea :submitted="submitted"
                              :rows="2"
                              v-model="internal_comment"
                              :maxChars="internalCommentMaxChars"
                              :id="'system-subscription-modal-notes'"
                              :label="$t('Notes')"/>
      </div>
    </div>

    <template #footer>
      <Button
          :label="$t('Cancel')"
          icon="pi pi-times"
          class="p-button-text"
          @click="close"/>

      <Button
          :label="$t('Save')"
          :disabled="dataIsSending"
          :loading="dataIsSending"
          icon="pi pi-check"
          class="p-button-text"
          @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import settings from '@/settings'
import formatMixins from '@/mixins/formatMixins'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import overlayVisibilityMixins from '@/mixins/overlayVisibilityMixins'

export default {
  name: 'SystemSubscriptionModal',
  emits: ['close', 'update-items'],
  mixins: [
    formatMixins,
    showErrorsMixins,
    overlayVisibilityMixins,
  ],
  props: {
    visible: Boolean,
    item: Object,
    planOptions: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    lockReasonOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internal_comment: '',
      internalCommentMaxChars: 500,
      settings,
      modalDataIsLoading: false,
      dataIsSending: false,
      submitted: false,
      itemData: {},
      selectedPlan: null,
      selectedStatus: null,
      selectedPendingPlan: null,
      selectedLockReason: null,
      currentPeriodStart: null,
      currentPeriodEnd: null,
      discountPercent: null,
      discountStartsAt: null,
      discountEndsAt: null,
      discountReason: null,
      pendingPlanStartsAt: null,
      cancelAtPeriodEnd: false,
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.loadItem()
      } else {
        this.reset()
      }
    },
    item: {
      immediate: true,
      handler() {
        if (this.visible) {
          this.loadItem()
        }
      },
    },
    selectedPendingPlan(value) {
      if (!value) {
        this.pendingPlanStartsAt = null
      }
    },
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: 0, width: '96%', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        // return {position: 'absolute', top: 0, width: '94%', maxWidth: this.exportInvoiceToSmartAccounts ? '1600px' : '1420px', marginTop: '15px'}
        return {position: 'absolute', top: 0, width: '94%', maxWidth: '1400px', marginTop: '15px'}
      } else {
        // return {position: 'absolute', top: 0, width: '90%', maxWidth: this.exportInvoiceToSmartAccounts ? '1600px' : '1420px'}
        return {position: 'absolute', top: 0, width: '90%', maxWidth: '1400px'}
      }
    },

    currentAmount() {
      const price = Number(this.selectedPlan?.price || 0)
      const discount = Number(this.discountPercent || 0)

      if (discount <= 0 || !this.discountIsActiveNow) {
        return price
      }

      return Math.round(price * (1 - discount / 100) * 100) / 100
    },

    discountIsActiveNow() {
      if (!(Number(this.discountPercent || 0) > 0)) {
        return false
      }

      const now = Date.now()

      if (this.validDate(this.discountStartsAt) && now < this.discountStartsAt.getTime()) {
        return false
      }

      if (this.validDate(this.discountEndsAt)) {
        const exclusiveEnd = this.toExclusiveEndTimestamp(this.discountEndsAt)

        if (exclusiveEnd && now >= exclusiveEnd * 1000) {
          return false
        }
      }

      return true
    },

    periodRangeInvalid() {
      return this.rangeInvalid(this.currentPeriodStart, this.currentPeriodEnd)
    },

    discountRangeInvalid() {
      return this.rangeInvalid(this.discountStartsAt, this.discountEndsAt)
    },

    pendingPlanOptions() {
      return this.planOptions.filter(plan => +plan.code !== +this.selectedPlan?.code)
    },
  },
  methods: {
    async loadItem() {
      this.reset()

      if (!this.item?.id) {
        return
      }

      this.itemData = {...this.item}
      this.modalDataIsLoading = true

      try {
        const {status, data} = await httpClient({
          url: 'location-subscription/view',
          method: 'post',
          data: {id: this.item.id},
        })

        if (status === 200 && data?.success && data.subscription) {
          this.fillFromSubscription(data.subscription)
        } else {
          this.fillFromSubscription(this.item)
          this.showUnknownError()
        }
      } catch (err) {
        this.fillFromSubscription(this.item)
        this.showError(err)
      } finally {
        this.modalDataIsLoading = false
      }
    },

    fillFromSubscription(subscription) {
      this.itemData = {...subscription}

      this.selectedPlan = this.planOptions.find(option => +option.code === +subscription.plan_code) || null
      this.selectedStatus = this.statusOptions.find(option => +option.code === +subscription.status) || null
      this.selectedPendingPlan = this.planOptions.find(option => +option.code === +subscription.pending_plan_code) || null
      this.selectedLockReason = this.lockReasonOptions.find(option => +option.code === +subscription.lock_reason) || null

      this.currentPeriodStart = this.fromTimestamp(subscription.current_period_start)
      this.currentPeriodEnd = this.fromExclusiveEndTimestamp(subscription.current_period_end)

      this.discountPercent = subscription.base_discount_percent !== null
          ? Number(subscription.base_discount_percent)
          : null
      this.discountStartsAt = this.fromTimestamp(subscription.base_discount_starts_at)
      this.discountEndsAt = this.fromExclusiveEndTimestamp(subscription.base_discount_ends_at)
      this.discountReason = subscription.base_discount_reason ?? null

      this.pendingPlanStartsAt = this.fromTimestamp(subscription.pending_plan_starts_at)
      this.cancelAtPeriodEnd = !!+subscription.cancel_at_period_end

      this.internal_comment = subscription.internal_comment ?? ''
    },

    validate() {
      this.submitted = true

      return !!(
          (this.internal_comment?.length ?? 0) <= this.internalCommentMaxChars
          && this.selectedPlan
          && this.selectedStatus
          && !this.periodRangeInvalid
          && !this.discountRangeInvalid
          && (!this.selectedPendingPlan || this.pendingPlanStartsAt)
          && (!this.cancelAtPeriodEnd || this.currentPeriodEnd)
      )
    },

    buildPayload() {
      return {
        id: +this.itemData.id,
        plan_code: +this.selectedPlan.code,
        status: +this.selectedStatus.code,

        current_period_start: this.toTimestamp(this.currentPeriodStart),
        current_period_end: this.toExclusiveEndTimestamp(this.currentPeriodEnd),

        base_discount_percent: Number(this.discountPercent || 0) > 0
            ? Number(this.discountPercent)
            : null,
        base_discount_starts_at: Number(this.discountPercent || 0) > 0
            ? this.toTimestamp(this.discountStartsAt)
            : null,
        base_discount_ends_at: Number(this.discountPercent || 0) > 0
            ? this.toExclusiveEndTimestamp(this.discountEndsAt)
            : null,
        base_discount_reason: Number(this.discountPercent || 0) > 0 && this.discountReason
            ? String(this.discountReason).trim()
            : null,

        pending_plan_code: this.selectedPendingPlan
            ? +this.selectedPendingPlan.code
            : null,
        pending_plan_starts_at: this.selectedPendingPlan
            ? this.toTimestamp(this.pendingPlanStartsAt)
            : null,

        cancel_at_period_end: this.cancelAtPeriodEnd ? 1 : 0,
        lock_reason: this.selectedStatus?.key === 'suspended' && this.selectedLockReason
            ? +this.selectedLockReason.code
            : null,

        internal_comment: String(this.internal_comment || '').trim() || null,
      }
    },

    async saveItem() {
      if (!this.validate()) {
        return
      }

      this.dataIsSending = true

      try {
        const {status, data} = await httpClient.post(
            'location-subscription/update',
            this.buildPayload()
        )

        if (status === 200 && data?.success) {
          this.$emit('update-items', this.itemData.id)
          this.$toast.add({
            severity: 'success',
            detail: this.$t('Data updated'),
            life: settings.toastLife,
          })
          this.close()
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.dataIsSending = false
      }
    },

    fromTimestamp(value) {
      if (!value) return null

      const source = new Date(+value * 1000)

      return new Date(
          source.getUTCFullYear(),
          source.getUTCMonth(),
          source.getUTCDate()
      )
    },

    fromExclusiveEndTimestamp(value) {
      if (!value) return null

      const source = new Date((+value - 1) * 1000)

      return new Date(
          source.getUTCFullYear(),
          source.getUTCMonth(),
          source.getUTCDate()
      )
    },

    toTimestamp(date) {
      if (!this.validDate(date)) return null

      return Math.floor(Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
      ) / 1000)
    },

    toExclusiveEndTimestamp(date) {
      if (!this.validDate(date)) return null

      return Math.floor(Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate() + 1
      ) / 1000)
    },

    validDate(date) {
      return date instanceof Date && !Number.isNaN(date.getTime())
    },

    rangeInvalid(start, end) {
      return this.validDate(start)
          && this.validDate(end)
          && end.getTime() < start.getTime()
    },

    statusLabel(statusKey) {
      const labels = {
        active: 'billing.subscriptions.statuses.active',
        suspended: 'billing.subscriptions.statuses.suspended',
        canceled: 'billing.subscriptions.statuses.canceled',
      }

      return labels[statusKey]
          ? this.$t(labels[statusKey])
          : this.$t('billing.subscriptions.statuses.unknown')
    },

    statusClass(statusKey) {
      const classes = {
        active: 'system-subscription-state--active',
        suspended: 'system-subscription-state--suspended',
        canceled: 'system-subscription-state--canceled',
      }

      return classes[statusKey] || 'system-subscription-state--unknown'
    },

    lockReasonLabel(reasonKey) {
      const labels = {
        billing_past_due: 'billing.subscriptions.system.lockReasons.billingPastDue',
        trial_expired: 'billing.subscriptions.system.lockReasons.trialExpired',
        manual: 'billing.subscriptions.system.lockReasons.manual',
        payment_failed: 'billing.subscriptions.system.lockReasons.paymentFailed',
        subscription_canceled: 'billing.subscriptions.system.lockReasons.canceled',
      }

      return labels[reasonKey]
          ? this.$t(labels[reasonKey])
          : reasonKey
    },

    close() {
      this.$emit('close')
      this.reset()
    },

    reset() {
      this.modalDataIsLoading = false
      this.dataIsSending = false
      this.submitted = false
      this.itemData = {}
      this.selectedPlan = null
      this.selectedStatus = null
      this.selectedPendingPlan = null
      this.selectedLockReason = null
      this.currentPeriodStart = null
      this.currentPeriodEnd = null
      this.discountPercent = null
      this.discountStartsAt = null
      this.discountEndsAt = null
      this.discountReason = null
      this.pendingPlanStartsAt = null
      this.cancelAtPeriodEnd = false
      this.internal_comment = ''
    },
  },
}
</script>

<style scoped lang="scss">
.system-subscription-modal__section-title {
  padding-bottom: 0.5rem;
  //font-size: 1rem;
  font-weight: 600;
}

.system-subscription-state--active {
  background: #c8e6c9;
  color: #256029;
}

.system-subscription-state--suspended {
  background: #ffcdd2;
  color: #c63737;
}

.system-subscription-state--canceled,
.system-subscription-state--unknown {
  background: #e0e0e0;
  color: #495057;
}
</style>